import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { UPDATE_WHATSAPP_AUTH_API, GET_WHATSAPP_CREDS_API, CREATE_WHATSAPP_AUTH_API } from '../config/API';
import Loader from '../components/common/Loader';
import { MdChat } from 'react-icons/md';
import './WhatsappAuth.css';
import { useUser } from '../context/UserContext';

const WhatsappAuth = () => {
    const { hasPermission } = useUser();
    const [employeeName, setEmployeeName] = useState('');
    const [employeeId, setEmployeeId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        authKey: '',
        appKey: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [isExistingRecord, setIsExistingRecord] = useState(false);

    useEffect(() => {
        fetchEmployeeWhatsAppCreds();
    }, []);

    const fetchEmployeeWhatsAppCreds = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;

            // Pre-populate from logged-in user if available
            if (currentUser) {
                if (currentUser.firstName) {
                    setEmployeeName(`${currentUser.firstName} ${currentUser.lastName || ''}`);
                } else if (currentUser.username) {
                    setEmployeeName(currentUser.username);
                }
                setEmployeeId(currentUser.id);
            }

            const response = await axios.get(GET_WHATSAPP_CREDS_API, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.data) {
                const creds = response.data.data;
                setFormData({
                    authKey: creds.authKey || '',
                    appKey: creds.appKey || ''
                });

                // If API returns specific employee name, use it
                if (creds.employeeName) {
                    setEmployeeName(creds.employeeName);
                } else if (creds.Employee && creds.Employee.firstName) {
                    setEmployeeName(`${creds.Employee.firstName} ${creds.Employee.lastName}`);
                } else if (creds.employee && creds.employee.firstName) {
                    setEmployeeName(`${creds.employee.firstName} ${creds.employee.lastName}`);
                }

                if (creds.employeeId) {
                    setEmployeeId(creds.employeeId);
                } else if (creds.employee && creds.employee.id) {
                    setEmployeeId(creds.employee.id);
                }

                // Check if keys actually exist
                if (creds.authKey || creds.appKey) {
                    setIsExistingRecord(true);
                } else {
                    setIsExistingRecord(false);
                }
            } else {
                // Keep the default logged-in user info but clear keys
                setFormData({ authKey: '', appKey: '' });
                setIsExistingRecord(false);
            }

        } catch (error) {
            console.log('No existing keys or error fetching:', error);
            // We already set defaults from localStorage, so just ensure isExistingRecord is false
            setIsExistingRecord(false);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.authKey || !formData.appKey) {
            Swal.fire('Warning', 'Please fill in both Auth Key and App Key', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('accessToken');

            // If employeeId is needed we can send it, but usually GET_WHATSAPP_CREDS_API is tied to logged-in user
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : {};
            const finalEmployeeId = employeeId || currentUser.id;

            const dataToSend = {
                employeeId: finalEmployeeId,
                authKey: formData.authKey,
                appKey: formData.appKey
            };

            let response;
            if (isExistingRecord) {
                response = await axios.put(UPDATE_WHATSAPP_AUTH_API(finalEmployeeId), dataToSend, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                response = await axios.post(CREATE_WHATSAPP_AUTH_API, dataToSend, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (response.data.status) {
                Swal.fire('Success', `Whatsapp keys ${isExistingRecord ? 'updated' : 'saved'} successfully!`, 'success');
                setIsExistingRecord(true);
            } else {
                Swal.fire('Error', response.data.message || 'Failed to save keys', 'error');
            }
        } catch (error) {
            console.error('Error updating keys:', error);
            Swal.fire('Error', error.response?.data?.message || 'An error occurred', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="add-po-page">
            <div className="page-header">
                <h2>Whatsapp Authentication Manager</h2>
            </div>

            <div className="project-form">
                <div className="form-section">
                    <h3>Update Employee Credentials</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Employee Name</label>
                                <input
                                    type="text"
                                    value={employeeName}
                                    placeholder="Employee name not found or keys don't exist yet"
                                    className="form-input"
                                    disabled
                                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#666' }}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>App Key</label>
                                <input
                                    type="text"
                                    name="appKey"
                                    value={formData.appKey}
                                    onChange={handleInputChange}
                                    placeholder="Enter App Key"
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Auth Key</label>
                                <input
                                    type="text"
                                    name="authKey"
                                    value={formData.authKey}
                                    onChange={handleInputChange}
                                    placeholder="Enter Auth Key"
                                    className="form-input"
                                />
                            </div>
                        </div>

                        <div className="form-actions" style={{ marginTop: '2rem' }}>
                            {(hasPermission('WHATSAPP_AUTH', 'create') || hasPermission('WHATSAPP_AUTH', 'update')) && (
                                <button
                                    type="submit"
                                    className="save-btn"
                                    disabled={submitting}
                                >
                                    {submitting ? (isExistingRecord ? 'Updating...' : 'Saving...') : (isExistingRecord ? 'Update Keys' : 'Save Keys')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WhatsappAuth;
